import { useEffect } from "react";
import { hasUnpublishedChanges } from "@/lib/publicationHelpers";
import type { Bien } from "@/types/bien";

/**
 * Avertit l'utilisateur lors d'une fermeture d'onglet ou d'un refresh
 * navigateur si le bien est `Publié` ET qu'il y a des modifs non poussées
 * vers les portails. Affiche le prompt natif `beforeunload` (libellé non
 * personnalisable, figé par le browser).
 *
 * Le helper `hasUnpublishedChanges()` est la source de vérité partagée
 * avec dashboard, fiche et Suivi Publication.
 *
 * **Limitation connue** : ce hook NE bloque PAS la navigation interne
 * React Router (clic sur un menu de la sidebar). Pour ça il faudrait
 * `useBlocker`, qui n'est dispo qu'avec `createBrowserRouter` (data
 * router). L'app utilise actuellement `<BrowserRouter>` classique. À
 * migrer dans un sprint dédié — la bannière "Modifs non poussées" sur
 * la fiche + le point orange Suivi Publication restent là pour
 * signaler visuellement le risque.
 */
export function useUnpublishedChangesGuard(
  bien: Pick<Bien, "statutPublication" | "updated_at" | "last_published_at">,
) {
  const shouldBlock = hasUnpublishedChanges(bien);

  useEffect(() => {
    if (!shouldBlock) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldBlock]);
}
